import CRMPageTemplate from '@components/Common/CRMPageTemplate';
import CRMStatusChip from '@components/Common/CRMStatusChip';
import EmptyState from '@components/Common/EmptyState';
import ErrorMessage from '@components/Common/ErrorMessage';
import FilterBar, { type AppliedFilter } from '@components/Common/FilterBar';
import SearchInput from '@components/Common/SearchInput';
import SelectFormField from '@components/Common/SelectFormField';
import SkeletonLoader from '@components/Common/Skeleton/SkeletonLoader';
import MintlyTable from '@components/Common/Table';
import type { ColumnsConfig } from '@components/Common/Table/cardFields';
import { getAmbassadorsQuery } from '@constants/graphQLQueries';
import { formatDate, formatDateTime, formatRelative } from '@helpers/date';
import { fetchGraphQLApiData } from '@helpers/graphQLApi';
import { useRememberListView } from '@helpers/listView';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import { Box } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { ambassadorName, STATUS_OPTIONS, type Ambassador } from './ambassador';
import './index.scss';

/* Named once so the loading skeleton draws the same number of columns as the table. */
const COLUMNS = ['name', 'code', 'city', 'firm', 'status', 'joined', 'lastLogin'];
const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 350;

/**
 * HR Ambassadors: external HR professionals who refer job seekers. Super Admin only, as is every
 * query behind it (BFF blocklist and backend).
 *
 * Search and status are server-side, so they live in the one `FilterBar`, and search applies itself
 * after a pause as it does on Guides. Search, status and page are in the query string, so Back from
 * an ambassador returns to the same view.
 */
const Ambassadors: React.FC = () => {
  const navigate = useNavigate();
  useRememberListView('/ambassadors');
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') ?? '';
  const status = searchParams.get('filter_status') ?? '';
  const page = Math.max(parseInt(searchParams.get('page') ?? '0', 10) || 0, 0);

  const [rows, setRows] = useState<Ambassador[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  /** One writer for the three keys. A filter change always goes back to the first page. */
  const setView = (next: { q?: string; status?: string; page?: number }) => {
    const params = new URLSearchParams(searchParams);
    const put = (key: string, value: string) => (value ? params.set(key, value) : params.delete(key));
    if (next.q !== undefined) put('q', next.q);
    if (next.status !== undefined) put('filter_status', next.status);
    const p = next.page ?? (next.q !== undefined || next.status !== undefined ? 0 : page);
    put('page', p > 0 ? String(p) : '');
    setSearchParams(params, { replace: true });
  };

  /* Search is typed into a draft and applied after a pause; Back/forward can change q underneath. */
  const [qDraft, setQDraft] = useState(q);
  useEffect(() => setQDraft(q), [q]);
  const setViewRef = useRef(setView);
  setViewRef.current = setView;
  useEffect(() => {
    if (qDraft.trim() === q.trim()) return undefined;
    const t = setTimeout(() => setViewRef.current({ q: qDraft.trim() }), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [qDraft, q]);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(null);
    fetchGraphQLApiData(getAmbassadorsQuery(status, q, page, PAGE_SIZE))
      .then((r) => {
        if (!live) return;
        const data = r?.data?.getAmbassadors;
        setRows((data?.items ?? []).filter(Boolean));
        setTotal(Number(data?.total ?? 0));
      })
      .catch(() => live && setError('HR Ambassadors could not be loaded. Please try again.'))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [status, q, page, reloadKey]);

  const applied: AppliedFilter[] = [
    ...(q ? [{ key: 'q', label: `Search: ${q}`, onClear: () => setView({ q: '' }) }] : []),
    ...(status
      ? [{
          key: 'status',
          label: `Status: ${STATUS_OPTIONS.find((o) => o.value === status)?.label ?? status}`,
          onClear: () => setView({ status: '' }),
        }]
      : []),
  ];

  const tableHeadRow: Record<string, React.ReactNode> = {
    name: 'Name',
    code: 'Code',
    city: 'City',
    firm: 'Firm',
    status: 'Status',
    joined: 'Joined',
    lastLogin: 'Last login',
  };
  const tableBodyRow: Record<string, (row: Ambassador) => React.ReactNode> = {
    name: (a) => (
      <Box className="amb-name">
        <span>{ambassadorName(a)}</span>
        {a.personalDetails?.email && <span className="amb-muted">{a.personalDetails.email}</span>}
      </Box>
    ),
    code: (a) => <span className="amb-code">{a.code || '-'}</span>,
    city: (a) => a.personalDetails?.city || '-',
    firm: (a) => a.firm || '-',
    status: (a) => (a.status ? <CRMStatusChip status={a.status.toLowerCase()} /> : '-'),
    joined: (a) => formatDate(a.createdAt),
    lastLogin: (a) => (
      <span title={a.lastLoginAt ? formatDateTime(a.lastLoginAt) : 'Never signed in'}>
        {formatRelative(a.lastLoginAt)}
      </span>
    ),
  };
  const columns: ColumnsConfig = {
    name: { card: 'title', tablet: true },
    code: { card: 'subtitle', tablet: true },
    status: { card: 'status', tablet: true },
    city: { card: 'fact' },
    firm: { card: 'fact' },
    joined: { card: 'fact' },
    lastLogin: { card: 'fact' },
  };

  return (
    <CRMPageTemplate
      title="HR Ambassadors"
      subtitle="External HR professionals who refer job seekers and earn milestone rewards."
      icon={<HandshakeOutlinedIcon />}
      totalCount={total}
    >
      <Box className="amb-list">
        <FilterBar
          applied={applied}
          onApply={() => setView({ q: qDraft.trim() })}
          onClearAll={() => {
            setQDraft('');
            setView({ q: '', status: '' });
          }}
        >
          <Box className="cc-filterbar__search">
            <SearchInput
              value={qDraft}
              onChange={setQDraft}
              placeholder="Search name, email, city or firm"
              onClear={() => setQDraft('')}
            />
          </Box>
          <SelectFormField
            required={false}
            name="ambassador-status"
            label="Status"
            options={STATUS_OPTIONS}
            value={status}
            onChange={(_n, v) => setView({ status: String(v ?? '') })}
          />
        </FilterBar>

        {error ? (
          <ErrorMessage message={error} onRetry={() => setReloadKey((k) => k + 1)} />
        ) : loading && rows.length === 0 ? (
          <SkeletonLoader rows={10} columns={COLUMNS.length} />
        ) : rows.length === 0 ? (
          <EmptyState
            compact
            title={q || status ? 'Nobody matches' : 'No HR Ambassadors yet'}
            body={q || status ? 'Try a different search or status.' : 'Ambassadors appear here once they register on the site.'}
          />
        ) : (
          <MintlyTable
            rows={rows}
            getRowKey={(a: Ambassador) => a._id}
            selectors={COLUMNS}
            columns={columns}
            tableHeadRow={tableHeadRow}
            tableBodyRow={tableBodyRow as Record<string, (row: any, index: number) => React.ReactNode>}
            onRowClick={(a: Ambassador) => navigate(`/ambassadors/${a._id}`)}
            pagination={{
              enabled: true,
              page,
              rowsPerPage: PAGE_SIZE,
              totalNumberOfRows: total,
              handleChangePage: (_e, n) => setView({ page: n }),
            }}
          />
        )}
      </Box>
    </CRMPageTemplate>
  );
};

export default Ambassadors;
